import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, BookOpen, Calendar, Award, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const DashboardPage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEnrollments = async () => {
      try {
        const response = await axios.get(`${API}/enrollments/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEnrollments(response.data);
      } catch (error) {
        console.error('Error fetching enrollments:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEnrollments();
  }, [token]);

  const handleLogout = () => {
    logout();
    toast.success('Signed out successfully');
    navigate('/', { replace: true });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-navy-50/30">

      {/* Content */}
      < div className="pt-24 pb-12 px-6" >
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-playfair font-bold text-navy-900 mb-2" data-testid="dashboard-title">
                Welcome, {user?.name}
              </h1>
              <p className="font-dm-sans text-navy-500">
                Manage your enrolled courses and access training materials
              </p>
            </div>

            {/* Stats */}
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <Card className="border-slate-200 shadow-card">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-sky/10 flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-sky" />
                  </div>
                  <div>
                    <p className="text-2xl font-playfair font-bold text-navy-900">
                      {enrollments.length}
                    </p>
                    <p className="text-sm font-dm-sans text-navy-500">Enrolled Courses</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-card">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Award className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-playfair font-bold text-navy-900">
                      {enrollments.filter(e => e.course?.level === 'level2').length}
                    </p>
                    <p className="text-sm font-dm-sans text-navy-500">Certifications</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-card">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-playfair font-bold text-navy-900">
                      {enrollments.length > 0 ? 'Active' : 'None'}
                    </p>
                    <p className="text-sm font-dm-sans text-navy-500">Upcoming Sessions</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Enrolled Courses */}
            <Card className="border-slate-200 shadow-card">
              <CardHeader>
                <CardTitle className="font-playfair text-navy-900">
                  My Enrolled Courses
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : enrollments.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="w-12 h-12 text-navy-300 mx-auto mb-4" />
                    <h3 className="text-lg font-playfair font-semibold text-navy-900 mb-2">
                      No Courses Yet
                    </h3>
                    <p className="font-dm-sans text-navy-500 mb-6">
                      Start your transformation journey by enrolling in a course
                    </p>
                    <div className="flex gap-4 justify-center">
                      <Link to="/wellness">
                        <Button className="bg-sky hover:bg-sky/90 font-dm-sans" data-testid="browse-wellness-btn">
                          Wellness Track
                        </Button>
                      </Link>
                      <Link to="/clinical">
                        <Button className="bg-navy-900 hover:bg-navy-800 font-dm-sans" data-testid="browse-clinical-btn">
                          Clinical Track
                        </Button>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {enrollments.map((item, index) => (
                      <div key={item.enrollment.id}>
                        <div className="flex items-center justify-between p-4 rounded-lg bg-navy-50/50 hover:bg-navy-50 transition-colors">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-playfair font-semibold text-navy-900">
                                {item.course.title}
                              </h3>
                              <Badge
                                className={`font-dm-sans text-xs ${item.course.track === 'wellness' ? 'bg-sky/10 text-sky' : 'bg-navy-100 text-navy-700'}`}
                              >
                                {item.course.track}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm font-dm-sans text-navy-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {item.course.schedule}
                              </span>
                              <span>{item.course.location}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <Badge className="bg-emerald-100 text-emerald-700 font-dm-sans">
                              Enrolled
                            </Badge>
                            <Button
                              size="sm"
                              className="bg-sky hover:bg-sky/90 font-dm-sans"
                              onClick={() => navigate(`/courses/${item.course.id}/learn`)}
                            >
                              <BookOpen className="w-3 h-3 mr-1" />
                              Start Learning
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="font-dm-sans"
                              onClick={() => navigate(`/courses/${item.course.id}`)}
                            >
                              View
                              <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                          </div>
                        </div>
                        {index < enrollments.length - 1 && <Separator className="my-2" />}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Links */}
            {enrollments.length > 0 && (
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <Card className="border-slate-200 shadow-card hover:shadow-card-hover transition-all cursor-pointer">
                  <CardContent className="p-6">
                    <h3 className="font-playfair font-semibold text-navy-900 mb-2">
                      Training Materials
                    </h3>
                    <p className="text-sm font-dm-sans text-navy-500 mb-4">
                      Access course resources, protocols, and supplementary materials
                    </p>
                    <Button variant="outline" size="sm" className="font-dm-sans">
                      Coming Soon
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 shadow-card hover:shadow-card-hover transition-all cursor-pointer">
                  <CardContent className="p-6">
                    <h3 className="font-playfair font-semibold text-navy-900 mb-2">
                      Consultation Calls
                    </h3>
                    <p className="text-sm font-dm-sans text-navy-500 mb-4">
                      Schedule monthly consultation calls with ETT instructors
                    </p>
                    <Button variant="outline" size="sm" className="font-dm-sans">
                      Coming Soon
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </motion.div>
        </div>
      </div >
    </div >
  );
};

export default DashboardPage;
